import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Star, 
  Heart, 
  MessageCircle, 
  Download,
  Send,
  MoreVertical,
  Trash2,
  Copy,
  Check,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  SkipBack,
  SkipForward
} from 'lucide-react'
import { Video, Comment } from '../../types'
import { useProjectStore } from '../../stores/projectStore'
import { useAuthStore } from '../../stores/authStore'

interface VideoLightboxProps {
  open: boolean
  onClose: () => void
  video: Video | undefined
  videos: Video[]
  onVideoSelect: (videoId: string) => void
  initialShowComments?: boolean
  projectTitle?: string
  sceneTitle?: string
}

const VideoLightbox: React.FC<VideoLightboxProps> = ({
  open,
  onClose,
  video,
  videos,
  onVideoSelect,
  initialShowComments = false,
  projectTitle = 'project',
  sceneTitle = 'scene'
}) => {
  const [showComments, setShowComments] = useState(initialShowComments)
  const [newComment, setNewComment] = useState('')
  const [replyTo, setReplyTo] = useState<string | null>(null)
  const [replyText, setReplyText] = useState('')
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [promptCopied, setPromptCopied] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const videoRef = useRef<HTMLVideoElement>(null)
  
  const { user } = useAuthStore()
  const { setMainVideo, toggleVideoFavorite, addVideoComment, deleteVideo, loading } = useProjectStore()

  const handleCopyPrompt = async () => {
    if (!video?.prompt) return
    try {
      await navigator.clipboard.writeText(video.prompt)
      setPromptCopied(true)
      setTimeout(() => setPromptCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy prompt:', error)
    }
  }

  // Set initial comment state when opening
  useEffect(() => {
    if (open) {
      setShowComments(initialShowComments)
    }
  }, [open, initialShowComments])
  
  // Esc key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [open, onClose])

  const currentIndex = videos.findIndex(vid => vid?.id === video?.id)
  const canGoPrev = currentIndex > 0
  const canGoNext = currentIndex < videos.length - 1

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return
      
      switch (e.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowLeft':
          if (canGoPrev) handlePrevious()
          break
        case 'ArrowRight':
          if (canGoNext) handleNext()
          break
        case ' ':
          e.preventDefault()
          togglePlayPause()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, canGoPrev, canGoNext])

  // Reset video state when changing videos
  useEffect(() => {
    if (video) {
      setIsPlaying(false)
      setCurrentTime(0)
      if (videoRef.current) {
        videoRef.current.currentTime = 0
      }
    }
  }, [video?.id])

  const handlePrevious = () => {
    if (canGoPrev) {
      onVideoSelect(videos[currentIndex - 1].id)
    }
  }

  const handleNext = () => {
    if (canGoNext) {
      onVideoSelect(videos[currentIndex + 1].id)
    }
  }

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted
      setIsMuted(!isMuted)
    }
  }

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime)
    }
  }

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration)
    }
  }

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value)
    if (videoRef.current) {
      videoRef.current.currentTime = time
      setCurrentTime(time)
    }
  }

  const skipForward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(videoRef.current.currentTime + 10, duration)
    }
  }

  const skipBackward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(videoRef.current.currentTime - 10, 0)
    }
  }

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen()
      } else {
        videoRef.current.requestFullscreen()
      }
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleSetMainVideo = async () => {
    if (!video) return
    try {
      await setMainVideo(video.id, video.sceneId)
    } catch (error) {
      console.error('Failed to set main video:', error)
    }
  }

  const handleToggleFavorite = async () => {
    if (!video) return
    try {
      await toggleVideoFavorite(video.id)
    } catch (error) {
      console.error('Failed to toggle favorite:', error)
    }
  }

  const handleDownload = async () => {
    if (!video) return
    try {
      const response = await fetch(video.url, { mode: 'cors' })
      if (!response.ok) throw new Error('Failed to fetch video')
      
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      const videoIndex = videos.findIndex(vid => vid.id === video.id) + 1
      const sanitizeFilename = (str: string) => str.replace(/[^a-z0-9]/gi, '_').toLowerCase()
      const sanitizedProject = sanitizeFilename(projectTitle)
      const sanitizedScene = sanitizeFilename(sceneTitle)
      
      let extension = 'mp4'
      if (blob.type) {
        extension = blob.type.split('/')[1] || 'mp4'
      }
      
      link.download = `${sanitizedProject}_${sanitizedScene}_video${videoIndex}.${extension}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download video:', error)
      alert('Failed to download video. Please try again.')
    }
  }

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!video || !newComment.trim() || !user) return

    try {
      await addVideoComment(video.id, newComment.trim(), user.uid, user.email || '')
      setNewComment('')
    } catch (error) {
      console.error('Failed to add comment:', error)
    }
  }

  const getTimeAgo = (date: string) => {
    const now = new Date()
    const past = new Date(date)
    const diffInMinutes = Math.floor((now.getTime() - past.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours}h ago`
    
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 30) return `${diffInDays}d ago`
    
    return past.toLocaleDateString()
  }

  if (!open || !video) return null

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        {/* Main Video Area */}
        <div className="flex-1 flex flex-col relative">
          {/* Navigation Arrows */}
          {canGoPrev && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handlePrevious()
              }}
              className="fixed left-4 top-1/2 -translate-y-1/2 glass-button p-3 hover:bg-white/20 z-20"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          )}

          {canGoNext && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleNext()
              }}
              className="fixed top-1/2 -translate-y-1/2 glass-button p-3 hover:bg-white/20 z-20"
              style={{ right: showComments ? '412px' : '16px' }}
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          )}

          {/* Video Container */}
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="relative max-w-full max-h-full" onClick={(e) => e.stopPropagation()}>
              <motion.video
                ref={videoRef}
                key={video.id}
                src={video.url}
                className="max-w-full max-h-[70vh] rounded-lg"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.15 }}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={() => setIsPlaying(false)}
                onClick={togglePlayPause}
              />
              
              {/* Play Overlay when paused */}
              {!isPlaying && (
                <div 
                  className="absolute inset-0 flex items-center justify-center cursor-pointer"
                  onClick={togglePlayPause}
                >
                  <div className="glass-button p-6 rounded-full bg-black/50 hover:bg-black/70 transition-colors">
                    <Play className="w-12 h-12 text-white" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Video Controls */}
          <div className="flex-shrink-0 px-4 pb-4" onClick={(e) => e.stopPropagation()}>
            <div className="glass-card p-4">
              {/* Progress Bar */}
              <div className="flex items-center space-x-3 mb-4">
                <span className="text-white/70 text-sm w-12">{formatTime(currentTime)}</span>
                <input
                  type="range"
                  min={0}
                  max={duration || 100}
                  value={currentTime}
                  onChange={handleSeek}
                  className="flex-1 h-1 accent-cyan-500"
                />
                <span className="text-white/70 text-sm w-12 text-right">{formatTime(duration)}</span>
              </div>

              {/* Control Buttons */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={skipBackward}
                    className="glass-button p-2 hover:bg-white/20"
                    title="Skip back 10s"
                  >
                    <SkipBack className="w-5 h-5 text-white" />
                  </button>
                  
                  <button
                    onClick={togglePlayPause}
                    className="glass-button p-3 bg-cyan-500/30 hover:bg-cyan-500/40"
                  >
                    {isPlaying ? (
                      <Pause className="w-6 h-6 text-white" />
                    ) : (
                      <Play className="w-6 h-6 text-white" />
                    )}
                  </button>
                  
                  <button
                    onClick={skipForward}
                    className="glass-button p-2 hover:bg-white/20"
                    title="Skip forward 10s"
                  >
                    <SkipForward className="w-5 h-5 text-white" />
                  </button>
                  
                  <button
                    onClick={toggleMute}
                    className="glass-button p-2 hover:bg-white/20 ml-2"
                  >
                    {isMuted ? (
                      <VolumeX className="w-5 h-5 text-white" />
                    ) : (
                      <Volume2 className="w-5 h-5 text-white" />
                    )}
                  </button>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-white/60 text-sm">{video.name}</span>
                  
                  <button
                    onClick={handleSetMainVideo}
                    className={`glass-button p-2 ${
                      video.isMain ? 'text-yellow-400' : 'text-white hover:text-yellow-400'
                    }`}
                    disabled={loading}
                  >
                    <Star className="w-4 h-4" fill={video.isMain ? 'currentColor' : 'none'} />
                  </button>

                  <button
                    onClick={handleToggleFavorite}
                    className={`glass-button p-2 ${
                      video.isFavorite ? 'text-red-400' : 'text-white hover:text-red-400'
                    }`}
                    disabled={loading}
                  >
                    <Heart className="w-4 h-4" fill={video.isFavorite ? 'currentColor' : 'none'} />
                  </button>

                  <button
                    onClick={() => setShowComments(!showComments)}
                    className="glass-button p-2 hover:bg-white/20 flex items-center space-x-1"
                  >
                    <MessageCircle className="w-4 h-4 text-white" />
                    <span className="text-white text-xs">
                      {video.comments.reduce((total, comment) => total + 1 + comment.replies.length, 0)}
                    </span>
                  </button>

                  <button
                    onClick={handleDownload}
                    className="glass-button p-2 text-white hover:text-blue-400"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={handleFullscreen}
                    className="glass-button p-2 hover:bg-white/20"
                  >
                    <Maximize className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
              
              {/* Prompt Display */}
              {video.prompt && (
                <button
                  onClick={handleCopyPrompt}
                  className="w-full mt-3 group"
                >
                  <div className={`relative bg-white/5 hover:bg-white/10 border rounded-xl p-3 transition-all text-left ${
                    promptCopied 
                      ? 'border-green-500/50 bg-green-500/10' 
                      : 'border-white/10 hover:border-cyan-500/30'
                  }`}>
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-white/80 text-sm leading-relaxed flex-1">
                        {video.prompt}
                      </p>
                      <div className={`flex-shrink-0 p-1.5 rounded-lg transition-all ${
                        promptCopied 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-white/10 text-white/50 group-hover:text-cyan-400 group-hover:bg-cyan-500/20'
                      }`}>
                        {promptCopied ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              )}
              
              <div className="flex items-center justify-between text-sm text-white/70 mt-2">
                <span>Uploaded {getTimeAgo(video.uploadedAt)}</span>
                <span>{currentIndex + 1} of {videos.length}</span>
              </div>
            </div>
          </div>

          {/* Top Controls */}
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between pointer-events-none">
            <div className="flex items-center space-x-2 pointer-events-auto">
              {video.isMain && (
                <span className="glass-button px-3 py-1 text-sm text-yellow-300 bg-yellow-500/20">
                  Main Video
                </span>
              )}
              {video.isFavorite && (
                <span className="glass-button px-3 py-1 text-sm text-red-300 bg-red-500/20">
                  Favorite
                </span>
              )}
            </div>

            <div className="flex items-center space-x-2 pointer-events-auto">
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowMoreMenu(!showMoreMenu)
                  }}
                  className="glass-button p-2 hover:bg-white/20"
                >
                  <MoreVertical className="w-5 h-5 text-white" />
                </button>

                <AnimatePresence>
                  {showMoreMenu && (
                    <>
                      <div 
                        className="fixed inset-0 z-40"
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowMoreMenu(false)
                        }}
                      />
                      <motion.div
                        className="absolute right-0 top-full mt-2 w-48 glass-card border border-white/20 z-50 shadow-xl"
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="py-1">
                          <button
                            onClick={() => {
                              handleSetMainVideo()
                              setShowMoreMenu(false)
                            }}
                            disabled={loading || video.isMain}
                            className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2 disabled:opacity-50"
                          >
                            <Star className="w-4 h-4" />
                            <span>{video.isMain ? 'Main Video' : 'Set as Main'}</span>
                          </button>
                          
                          <button
                            onClick={() => {
                              handleToggleFavorite()
                              setShowMoreMenu(false)
                            }}
                            disabled={loading}
                            className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2 disabled:opacity-50"
                          >
                            <Heart className="w-4 h-4" />
                            <span>{video.isFavorite ? 'Remove Favorite' : 'Add Favorite'}</span>
                          </button>

                          <button
                            onClick={() => {
                              handleDownload()
                              setShowMoreMenu(false)
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2"
                          >
                            <Download className="w-4 h-4" />
                            <span>Download</span>
                          </button>

                          <hr className="border-white/10 my-1" />
                          
                          <button
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this video?')) {
                                deleteVideo(video.id)
                                setShowMoreMenu(false)
                                onClose()
                              }
                            }}
                            disabled={loading}
                            className="w-full px-3 py-2 text-left text-sm text-red-300 hover:bg-red-500/20 flex items-center space-x-2 disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete</span>
                          </button>
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onClose()
                }}
                className="glass-button p-2 hover:bg-white/20"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Comments Sidebar */}
        <AnimatePresence>
          {showComments && (
            <motion.div
              className="w-96 bg-black/80 backdrop-blur-sm border-l border-white/10 flex flex-col"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Comments Header */}
              <div className="p-4 border-b border-white/10">
                <h3 className="text-white font-medium">Comments</h3>
              </div>

              {/* Comments List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {video.comments.map((comment) => (
                  <div key={comment.id} className="space-y-3">
                    <div className="glass-card p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 bg-cyan-500/30 rounded-full flex items-center justify-center">
                            <span className="text-xs text-white">
                              {comment.userEmail.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-white text-sm font-medium">
                            {comment.userEmail}
                          </span>
                          <span className="text-white/50 text-xs">
                            {getTimeAgo(comment.createdAt)}
                          </span>
                        </div>
                      </div>
                      
                      <p className="text-white/90 text-sm mb-2">{comment.content}</p>
                      
                      <button
                        onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
                        className="text-cyan-300 text-xs hover:text-cyan-200"
                      >
                        Reply
                      </button>
                    </div>

                    {/* Replies */}
                    {comment.replies.map((reply) => (
                      <div key={reply.id} className="ml-6 glass-card p-3">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="w-5 h-5 bg-cyan-500/30 rounded-full flex items-center justify-center">
                            <span className="text-xs text-white">
                              {reply.userEmail.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-white text-sm font-medium">
                            {reply.userEmail}
                          </span>
                          <span className="text-white/50 text-xs">
                            {getTimeAgo(reply.createdAt)}
                          </span>
                        </div>
                        <p className="text-white/90 text-sm">{reply.content}</p>
                      </div>
                    ))}
                  </div>
                ))}

                {video.comments.length === 0 && (
                  <div className="text-center py-8">
                    <MessageCircle className="w-12 h-12 text-white/30 mx-auto mb-4" />
                    <p className="text-white/70">No comments yet</p>
                    <p className="text-white/50 text-sm">Be the first to comment!</p>
                  </div>
                )}
              </div>

              {/* Add Comment Form */}
              <div className="p-4 border-t border-white/10">
                <form onSubmit={handleAddComment} className="space-y-3">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault()
                        if (newComment.trim()) {
                          handleAddComment(e as any)
                        }
                      }
                    }}
                    placeholder="Add a comment... (Ctrl+Enter to submit)"
                    className="glass-input w-full h-20 resize-none"
                    maxLength={500}
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/50">
                      {newComment.length}/500
                    </span>
                    <button
                      type="submit"
                      disabled={!newComment.trim() || loading}
                      className="glass-button bg-cyan-500/30 hover:bg-cyan-500/40 text-white px-4 py-2 flex items-center space-x-2 disabled:opacity-50"
                    >
                      <Send className="w-4 h-4" />
                      <span>Comment</span>
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  )
}

export default VideoLightbox

